import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import classNames from 'classnames';
import { WizardStepContainer } from './partials';
import WizardVideoDemo from './partials/WizardVideoDemo';
import { wizardOpenThemeEditor, wizardRequestScriptCheck } from '../../../../../slices/WizardSlice';
import { useLocalizedMessage } from '../../../../../util/useLocalizedMessage';
import IntlMessages from 'util/IntlMessages';

const DEFAULT_VIDEO_URL = 'https://global.cdn.kealabs.com/_resources/videos/admin-app/shopify-how-to-enable-app-embeds.mp4';

export default function WizardScriptIntegrationStep({ onReady }) {
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();

  const {
    platform,
    scriptConnected: settingsScriptConnected,
    shopifyEmbedVideoUrl,
  } = useSelector((s) => s.settings);
  const { stepLoading, scriptConnected: wizardScriptConnected } = useSelector((s) => s.wizard);

  const [checkAttempted, setCheckAttempted] = useState(false);

  const isShopify = platform === 'shopify';
  const effectiveConnected = Boolean(settingsScriptConnected || wizardScriptConnected);

  const status = useMemo(() => {
    if (effectiveConnected) return 'connected';
    if (checkAttempted && stepLoading) return 'checking';
    if (checkAttempted && !stepLoading) return 'not_found';
    return 'idle';
  }, [effectiveConnected, checkAttempted, stepLoading]);

  useEffect(() => {
    if (onReady) onReady(effectiveConnected);
  }, [effectiveConnected, onReady]);

  const handleOpenThemeEditor = useCallback(() => {
    dispatch(wizardOpenThemeEditor());
  }, [dispatch]);

  const handleCheck = useCallback(() => {
    if (stepLoading) return;
    setCheckAttempted(true);
    dispatch(wizardRequestScriptCheck());
  }, [dispatch, stepLoading]);

  const videoSrc = shopifyEmbedVideoUrl || DEFAULT_VIDEO_URL;

  return (
    <div className={classNames('wizard-step', 'wizard-step-integration')}>
      <WizardStepContainer title={localizeMessage('wizard.script.title')}>
        <div className="row">
          <div className="col-8 offset-2">
            <p className="sub-header">
              {localizeMessage('wizard.script.subheader')}
            </p>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-lg-3 col-md-4 col-12 mb-4">
            <div className="card p-3 h-100 d-flex">
              <Typography variant="h6" className="mb-2">
                {localizeMessage('wizard.script.quickActions')}
              </Typography>

              <div className="d-flex flex-column">
                {isShopify && (
                  <Button
                    variant="contained"
                    color="primary"
                    className="mb-2"
                    onClick={handleOpenThemeEditor}
                    disabled={stepLoading}
                  >
                    {localizeMessage('wizard.script.button.openThemeEditor')}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  className="mb-2"
                  onClick={handleCheck}
                  disabled={stepLoading}
                >
                  {stepLoading ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: 8 }} />
                      {localizeMessage('wizard.script.button.checking')}
                    </>
                  ) : localizeMessage('wizard.script.button.checkNow')}
                </Button>
              </div>

              <div className="mt-2">
                {status === 'connected' && (
                  <Typography variant="subtitle1" className="text-app-primary">
                    {localizeMessage('wizard.script.status.connected')}
                  </Typography>
                )}
                {status === 'checking' && (
                  <Typography variant="subtitle1" className="text-muted">
                    {localizeMessage('wizard.script.status.checking')}
                  </Typography>
                )}
                {status === 'not_found' && !effectiveConnected && (
                  <Typography variant="subtitle1" className="text-danger">
                    {localizeMessage('wizard.script.status.notFound')}
                  </Typography>
                )}
                {status === 'idle' && (
                  <Typography variant="subtitle1" className="text-muted">
                    {localizeMessage('wizard.script.status.idle')}
                  </Typography>
                )}
              </div>
              <div style={{ marginTop: '100px' }}>
                {!effectiveConnected && (
                  <Typography variant="body2" className="text-muted">
                    {localizeMessage('wizard.script.status.skipWarning')}
                  </Typography>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-9 col-md-8 col-12">
            <WizardVideoDemo
              src={videoSrc}
              title={<IntlMessages id="ui.welcome.wizzard.wizardSteps.wizardScriptIntegrationStep.shopifyHowToActivateKeaLabsVisualSearch" />}
              height="70vh"
            />
          </div>
        </div>
      </WizardStepContainer>
    </div>
  );
}
